import React from "react";
import { useTranslation } from "react-i18next";
import { AcquisitionChannel } from "../../features/admin/contracts/AdminAcquisitionSourceResponse";
import { AdminAcquisitionSource } from "../../features/admin/models/AdminAcquisition";

const headerClass = "bg-grey-95 dark:bg-primary-20 text-grey-30 dark:text-grey-90";
/** Body text colour, set once on the tbody so every cell is readable in dark mode. */
const bodyClass = "text-grey-20 dark:text-white";
const rowClass = "border-t border-grey-90 dark:border-primary-20";
const numberCellClass = "px-4 py-2 text-right tabular-nums";

/** The locale key of each channel's label. */
const CHANNEL_KEYS: Record<AcquisitionChannel, string> = {
  Direct: "direct",
  Search: "search",
  AiAnswer: "aiAnswer",
  Social: "social",
  MetaAd: "metaAd",
  Email: "email",
  Paid: "paid",
  Referral: "referral",
};

interface AdminAcquisitionSourcesTableProps {
  sources: AdminAcquisitionSource[];
}

/**
 * Where visitors entered the site from — search, an AI answer, a social network, an ad, a
 * newsletter, a linking site, or nowhere (typed) — with their views, engaged reads and CTA taps.
 * Most views first, as the API orders them.
 */
const AdminAcquisitionSourcesTable: React.FC<AdminAcquisitionSourcesTableProps> = ({ sources }) => {
  const { t } = useTranslation();
  if (sources.length === 0) {
    return (
      <div className="with-border rounded-2xl p-8 text-center text-grey-40 dark:text-grey-70">
        {t("admin.acquisition.noPageViews")}
      </div>
    );
  }
  return (
    <div className="with-border overflow-x-auto rounded-2xl">
      <table className="w-full text-sm">
        <thead className={headerClass}>
          <tr>
            <th className="px-4 py-2 text-left">{t("admin.acquisition.channelHeader")}</th>
            <th className="px-4 py-2 text-left">{t("admin.acquisition.source")}</th>
            <th className="px-4 py-2 text-right">{t("admin.acquisition.pageViews")}</th>
            <th className="px-4 py-2 text-right">{t("admin.acquisition.engaged")}</th>
            <th className="px-4 py-2 text-right">{t("admin.acquisition.ctaTaps")}</th>
          </tr>
        </thead>
        <tbody className={bodyClass}>
          {sources.map((source) => (
            <tr key={`${source.channel}:${source.source}`} className={rowClass}>
              <td className="whitespace-nowrap px-4 py-2">
                {t(`admin.acquisition.channel.${CHANNEL_KEYS[source.channel]}`)}
              </td>
              <td className="break-all px-4 py-2 font-mono text-xs">{source.source}</td>
              <td className={numberCellClass}>{source.pageViews}</td>
              <td className={numberCellClass}>{source.engaged}</td>
              <td className={numberCellClass}>{source.ctaTaps}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminAcquisitionSourcesTable;
